import React, { useState } from "react";
import { PageHero, SectionHeader, SiteLayout } from "./components/SiteLayout";

const BASE = "https://raw.githubusercontent.com/mahmad2005/grip-force-measurement-device/main";

const rdProjects = [
  {
    id: "grip-force",
    title: "Hand Grip Force & Trajectory Measurement System",
    sector: "Biomedical / Embedded Systems",
    status: "Ongoing Research",
    github: "https://github.com/mahmad2005/grip-force-measurement-device",
    youtube: "https://www.youtube.com/watch?v=EXzzfofSnuo",
    overview:
      "A portable, intelligent device designed to measure hand grip force and object trajectory for neurological assessment — particularly for patients with stroke or Multiple Sclerosis (MS). The system enables clinicians and researchers to quantitatively assess hand grip strength, track and visualize object movement during gripping tasks, and analyze neuromuscular impairments and rehabilitation outcomes.",
    keyFeatures: [
      { icon: "🧠", label: "Neurological Focus", desc: "Evaluates motor function, coordination, and rehabilitation progress in stroke and MS patients." },
      { icon: "⚙️", label: "STM32 MCU", desc: "Real-time data acquisition and processing using STM32F103 microcontroller." },
      { icon: "🖐️", label: "32×64 Pressure Matrix", desc: "High-resolution spatial grip force measurement via custom ADG732-based multiplexer network." },
      { icon: "📡", label: "Gyroscope & IMU", desc: "SparkFun ICM-20948 for motion, orientation, and trajectory tracking of held objects." },
      { icon: "🔵", label: "Bluetooth / Wi-Fi", desc: "ESP32-C3 for wireless data transmission to PC/mobile apps via UDP." },
      { icon: "🖥️", label: "Real-Time Visualizer", desc: "Python-based 3D/2D visualization of pressure map and sensor data with AprilTag tracking." },
    ],
    stack: ["C/C++ (STM32 HAL)", "FreeRTOS", "Python", "ESP32", "KiCad", "BLE", "OpenCV"],
    galleryGroups: [
      {
        label: "New PCB Design — 3D Views",
        images: [
          { src: `${BASE}/hardware/pcb%20design/images/3dviewfront.png`, caption: "3D View Front" },
          { src: `${BASE}/hardware/pcb%20design/images/3dviewback.png`, caption: "3D View Back" },
          { src: `${BASE}/hardware/pcb%20design/images/3dviewfront_printed.jpg`, caption: "Printed PCB Front" },
          { src: `${BASE}/hardware/pcb%20design/images/3dviewback_printed.jpg`, caption: "Printed PCB Back" },
        ],
      },
      {
        label: "Schematic & Testing",
        images: [
          { src: `${BASE}/hardware/pcb%20design/images/schemetic.png`, caption: "Circuit Schematic" },
          { src: `${BASE}/hardware/pcb%20design/images/Testing.jpg`, caption: "Hardware Testing" },
        ],
      },
      {
        label: "Previous Design",
        images: [
          { src: `${BASE}/hardware/previous%20design/images/3dview.png`, caption: "3D View" },
          { src: `${BASE}/hardware/previous%20design/images/3dview_bth_module.png`, caption: "3D View — BTH Module" },
          { src: `${BASE}/hardware/previous%20design/images/pcb.png`, caption: "PCB Layout" },
          { src: `${BASE}/hardware/previous%20design/images/pcb_bth_module.png`, caption: "PCB — BTH Module" },
        ],
      },
      {
        label: "Assembly & Testing Photos",
        images: [
          { src: `${BASE}/hardware/previous%20design/images/CircuitInsideCylindricalCase.jpeg`, caption: "Circuit Inside Cylindrical Case" },
          { src: `${BASE}/hardware/previous%20design/images/measuringGripForce.jpeg`, caption: "Grip Force Test" },
          { src: `${BASE}/hardware/previous%20design/images/PCBatWork.jpeg`, caption: "PCB at Work" },
          { src: `${BASE}/hardware/previous%20design/images/32x64_sensorPad.jpeg`, caption: "32×64 Sensor Pad" },
          { src: `${BASE}/hardware/previous%20design/images/pressureSensingDemostration.jpeg`, caption: "Pressure Sensing Demo" },
        ],
      },
      {
        label: "Application View",
        images: [
          { src: `${BASE}/app/app_view.png`, caption: "Live App — Pressure Map & IMU Data" },
          { src: `${BASE}/hardware/sensors%20design/pressure%20sensor%20pad%20design/pressureSensorPadFootPrint_v2_3D.png`, caption: "Pressure Sensor Pad 3D" },
        ],
      },
    ],
    futureWork: [
      "Integration with AI models for predictive analysis of grip patterns",
      "Mobile app for real-time data visualization",
      "Cloud-based data synchronization and reporting dashboard",
    ],
  },
];

function RdProjectCard({ project }) {
  const [openGroup, setOpenGroup] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  return (
    <article className="panel-card flex flex-col gap-8">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700">{project.sector}</p>
          <h3 className="mt-2 font-display text-3xl font-bold text-emerald-950">{project.title}</h3>
          <span className="mt-3 inline-block rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">
            {project.status}
          </span>
        </div>
        <div className="flex gap-3">
          <a
            href={project.github}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 rounded-full border border-emerald-900/20 bg-white px-4 py-2 text-sm font-semibold text-emerald-900 transition hover:bg-emerald-50"
          >
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.749 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" />
            </svg>
            GitHub
          </a>
          <a
            href={project.youtube}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700"
          >
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
            </svg>
            Demo Video
          </a>
        </div>
      </div>

      {/* Overview */}
      <p className="text-base leading-8 text-slate-600">{project.overview}</p>

      {/* Key Features */}
      <div>
        <h4 className="font-display text-lg font-bold text-emerald-950">Key Features & Technologies</h4>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {project.keyFeatures.map((f) => (
            <div key={f.label} className="rounded-[18px] border border-emerald-900/10 bg-emerald-50/70 p-4">
              <p className="text-lg">{f.icon} <span className="font-semibold text-emerald-900">{f.label}</span></p>
              <p className="mt-1 text-sm text-slate-600">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Tech stack */}
      <div className="flex flex-wrap gap-2">
        {project.stack.map((t) => (
          <span key={t} className="chip-tag">{t}</span>
        ))}
      </div>

      {/* Image gallery by group */}
      <div>
        <h4 className="font-display text-lg font-bold text-emerald-950">Project Gallery</h4>
        <div className="mt-4 space-y-4">
          {project.galleryGroups.map((group) => (
            <div key={group.label} className="rounded-[20px] border border-emerald-900/10 overflow-hidden">
              <button
                onClick={() => setOpenGroup(openGroup === group.label ? null : group.label)}
                className="w-full flex items-center justify-between px-5 py-4 bg-emerald-50/80 text-left"
              >
                <span className="font-semibold text-emerald-950">{group.label}</span>
                <svg
                  className={`h-4 w-4 text-emerald-700 transition-transform ${openGroup === group.label ? "rotate-180" : ""}`}
                  fill="none" viewBox="0 0 10 6"
                >
                  <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              {openGroup === group.label && (
                <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 bg-white">
                  {group.images.map((img) => (
                    <button
                      key={img.src}
                      onClick={() => setLightbox(img)}
                      className="group relative overflow-hidden rounded-[14px] bg-slate-100 aspect-[4/3] hover:shadow-lg transition"
                    >
                      <img
                        src={img.src}
                        alt={img.caption}
                        className="h-full w-full object-cover transition group-hover:scale-105"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                      <div className="absolute inset-x-0 bottom-0 bg-black/50 px-3 py-2 text-xs text-white text-left">
                        {img.caption}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Future Work */}
      <div>
        <h4 className="font-display text-lg font-bold text-emerald-950">Future Work</h4>
        <ul className="mt-3 space-y-2">
          {project.futureWork.map((item) => (
            <li key={item} className="flex items-start gap-2 text-sm text-slate-600">
              <span className="mt-0.5 text-emerald-600">→</span>
              {item}
            </li>
          ))}
        </ul>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setLightbox(null)}
        >
          <div className="relative max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.src} alt={lightbox.caption} className="w-full rounded-2xl object-contain max-h-[80vh]" />
            <p className="mt-3 text-center text-white text-sm">{lightbox.caption}</p>
            <button
              onClick={() => setLightbox(null)}
              className="absolute -top-4 -right-4 bg-white rounded-full p-1.5 shadow-lg text-slate-700 hover:text-red-600"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </article>
  );
}

export default function RndPage() {
  return (
    <SiteLayout title="R&D" description="Research and development projects by SaskTech Lab team members exploring biomedical engineering, embedded systems, and emerging technologies.">
      <PageHero
        eyebrow="Research & Development"
        title="Pushing the Boundaries of Engineering"
        description="Exploratory projects by our team members — where emerging technologies meet real-world problems in biomedical devices, embedded systems, and intelligent hardware."
      />

      <section className="site-shell mt-8 pb-10">
        <SectionHeader
          eyebrow="R&D Works"
          title="Our Research Projects"
          description="These projects represent ongoing and completed research initiatives outside of commercial work — driven by curiosity, innovation, and a commitment to advancing engineering knowledge."
        />
        <div className="mt-10 space-y-10">
          {rdProjects.map((project) => (
            <RdProjectCard key={project.id} project={project} />
          ))}
        </div>

        {/* Sponsors / Partners */}
        <div className="mt-12 rounded-[28px] border border-emerald-900/10 bg-emerald-50/50 p-8">
          <p className="text-center text-xs font-bold uppercase tracking-widest text-slate-500 mb-8">
            Research Partners & Sponsors
          </p>
          <div className="flex flex-wrap items-center justify-center gap-10">
            {/* University of Regina */}
            <a
              href="https://www.uregina.ca/kinesiology-health-studies/index.html"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-3 opacity-80 transition hover:opacity-100"
            >
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/0/06/UR_Logo_Primary_Full_Colour_RGB.jpg"
                alt="University of Regina"
                className="h-20 w-auto object-contain"
              />
              <span className="text-xs font-semibold text-slate-600 text-center">
                University of Regina<br />
                <span className="font-normal text-slate-500">Dept. of Kinesiology & Health Studies</span>
              </span>
            </a>

            <div className="hidden sm:block h-16 w-px bg-emerald-900/10" />

            {/* URNeuroLab */}
            <a
              href="https://www.urneurolab.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-3 opacity-80 transition hover:opacity-100"
            >
              <img
                src="https://static.wixstatic.com/media/4af91b_2dca7d56a89a43acab4338bbd1d752b0~mv2.png"
                alt="URNeuroLab"
                className="h-20 w-auto object-contain"
              />
              <span className="text-xs font-semibold text-slate-600 text-center">
                URNeuroLab<br />
                <span className="font-normal text-slate-500">Neuroplasticity & Neurorehabilitation Research Lab</span>
              </span>
            </a>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
